import { initAnalytics } from './firebase'
import {
  type Analytics,
  logEvent,
  setAnalyticsCollectionEnabled,
  setDefaultEventParameters,
  setUserProperties,
  setCurrentScreen,
} from 'firebase/analytics'
import { useAuthStore } from '../stores/auth'
import { posthogService, type CaptureContext } from './posthogService'
import { getPageContext, getDeviceContext } from '../utils/posthogHelpers'
import { getRouteTemplate } from '../utils/routeTemplate'
import { scrubAnalyticsProperties } from '../utils/piiScrubber'
import { logger } from '../utils/logger'

export interface AnalyticsEvent {
  name: string
  parameters?: Record<string, unknown>
  userId?: string
  timestamp?: number
}

export interface UserProperties {
  userType?: 'therapist' | 'child'
  subscriptionTier?: 'free' | 'premium' | 'enterprise'
  onboardingCompleted?: boolean
  featureFlags?: Record<string, boolean>
}

export interface PageViewEvent {
  page_title: string
  page_location: string
  page_path: string
  user_type?: string
  session_id?: string
}

export interface UserEngagementEvent {
  engagement_time_msec: number
  session_id?: string
  user_type?: string
}

export interface OnboardingEvent {
  step_name: string
  step_number: number
  completion_time?: number
  user_type?: string
}

export interface DashboardEvent {
  action: string
  feature: string
  value?: number | string
  user_type?: string
}

class AnalyticsService {
  private consentGiven = false
  private sessionId: string
  private startTime: number
  private analyticsInstance: Analytics | null = null

  constructor() {
    this.sessionId = this.generateSessionId()
    this.startTime = Date.now()
    // Defer initialization to break circular dependency with posthogService
    setTimeout(() => this.initializeConsent(), 0)
  }

  // Firebase Analytics must never be started before consent: getAnalytics()
  // begins GA4's automatic collection immediately. Only call this once
  // this.consentGiven is true.
  private async ensureAnalyticsInitialized(): Promise<Analytics | null> {
    if (!this.consentGiven) return null

    if (!this.analyticsInstance) {
      this.analyticsInstance = await initAnalytics()
    }
    if (this.analyticsInstance) {
      setAnalyticsCollectionEnabled(this.analyticsInstance, true)
      // gtag stamps dl/dp (document location/path) onto EVERY hit it sends,
      // including automatic ones, from this "set" state - not from any
      // event's own parameters. Refresh it to the current route template on
      // every call so no hit, of any kind, ever carries the concrete path.
      const routeTemplate = getRouteTemplate(window.location.pathname)
      setDefaultEventParameters({
        page_location: `${window.location.origin}${routeTemplate}`,
        page_path: routeTemplate,
      })
    }
    return this.analyticsInstance
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  private initializeConsent() {
    // Check for existing consent
    const consent = localStorage.getItem('analytics_consent')

    if (consent === 'true') {
      this.consentGiven = true
      this.initializeUser()
      // Forward stored consent to PostHog
      try {
        posthogService.setConsent(true)
      } catch (err) {
        logger.error('[PostHog] Failed to forward stored consent to PostHog:', err)
      }
    } else if (consent === 'false') {
      this.consentGiven = false
    } else {
      // No consent given yet - default based on env flag (dev convenience)
      const defaultConsent = (import.meta.env.VITE_ANALYTICS_DEFAULT_CONSENT as string | undefined) === 'true'
      if (defaultConsent) {
        this.setConsent(true)
      } else {
        this.consentGiven = false
      }
    }
  }

  public setConsent(given: boolean) {
    this.consentGiven = given
    localStorage.setItem('analytics_consent', given.toString())

    if (given) {
      this.initializeUser()
    } else if (this.analyticsInstance) {
      // Revoking consent stops collection without tearing down the instance.
      setAnalyticsCollectionEnabled(this.analyticsInstance, false)
    }

    // Forward consent to PostHog
    try {
      posthogService.setConsent(given)
    } catch (err) {
      logger.error('[PostHog] Failed to forward consent to PostHog:', err)
    }
  }

  public hasConsent(): boolean {
    return this.consentGiven
  }

  private async initializeUser() {
    if (!this.consentGiven) return

    const analytics = await this.ensureAnalyticsInitialized()

    try {
      const { currentTherapist } = useAuthStore.getState()
      if (currentTherapist) {
        if (analytics) {
          // PostHog is the product analytics identity. Never send a raw
          // therapist uid to Firebase Analytics.
          const userProperties: Record<string, unknown> = {
            userType: 'therapist',
            onboardingCompleted: true, // Assuming therapist is already onboarded
          }
          
          await setUserProperties(analytics, userProperties)
        }

        // Inform PostHog of current user type (therapist)
        posthogService.setUserType('therapist')
      } else {
        // Anonymous/child flows
        posthogService.setUserType('anonymous')
      }
    } catch (error) {
      logger.error('Failed to initialize analytics user:', error)
    }
  }

  public async trackEvent(event: AnalyticsEvent): Promise<void> {
    if (!this.consentGiven) {
      return
    }

    try {
      const routeTemplate = getRouteTemplate(window.location.pathname)
      const rawEventData = {
        ...event.parameters,
        source: 'app',
        route_template: routeTemplate,
        session_id: this.sessionId,
        timestamp: event.timestamp || Date.now(),
      }

      // Scrub once, before the event reaches ANY sink. Never send raw user_id.
      const eventData = scrubAnalyticsProperties(rawEventData)
      delete eventData.user_id

      if (import.meta.env.DEV) {
        logger.debug(`[Analytics] ${event.name}`, { keys: Object.keys(eventData).length })
      }

      const analytics = await this.ensureAnalyticsInitialized()
      if (analytics) {
        await logEvent(analytics, event.name, eventData)
      }

      const context: CaptureContext = {
        page: getPageContext(),
        session: {
          duration_ms: this.getSessionDuration(),
        },
        device: getDeviceContext(),
      }

      posthogService.captureWithContext(event.name, eventData, context)
    } catch (error) {
      logger.error(`[Analytics] Failed to track event '${event.name}':`, error)
    }
  }

  public async trackPageView(pageView: PageViewEvent): Promise<void> {
    if (!this.consentGiven) {
      return
    }

    try {
      // Never forward the concrete path: always the matched route template.
      const routeTemplate = getRouteTemplate(pageView.page_path)
      const pageLocation = `${window.location.origin}${routeTemplate}`
      const eventData = scrubAnalyticsProperties({
        page_title: pageView.page_title,
        page_location: pageLocation,
        page_path: routeTemplate,
        user_type: pageView.user_type,
        session_id: pageView.session_id || this.sessionId,
      })

      const analytics = await this.ensureAnalyticsInitialized()
      if (analytics) {
        await setCurrentScreen(analytics, pageView.page_title)
        await logEvent(analytics, 'page_view', eventData)
      }

      // PostHog pageview
      posthogService.pageview(pageView.page_title, routeTemplate, {
        page_location: pageLocation,
      })
    } catch (error) {
      logger.error('Failed to track page view:', error)
    }
  }

  public async trackUserEngagement(engagement: UserEngagementEvent): Promise<void> {
    if (!this.consentGiven) return

    try {
      await this.trackEvent({
        name: 'user_engagement',
        parameters: {
          engagement_time_msec: engagement.engagement_time_msec,
          session_id: engagement.session_id || this.sessionId,
          user_type: engagement.user_type,
        }
      })
    } catch (error) {
      logger.error('Failed to track user engagement:', error)
    }
  }

  public async trackOnboardingStep(step: OnboardingEvent): Promise<void> {
    if (!this.consentGiven) return

    try {
      await this.trackEvent({
        name: 'onboarding_step',
        parameters: {
          step_name: step.step_name,
          step_number: step.step_number,
          completion_time: step.completion_time,
          user_type: step.user_type,
          session_id: this.sessionId,
        }
      })
    } catch (error) {
      logger.error('Failed to track onboarding step:', error)
    }
  }

  public async trackDashboardInteraction(interaction: DashboardEvent): Promise<void> {
    if (!this.consentGiven) return

    try {
      await this.trackEvent({
        name: 'dashboard_interaction',
        parameters: {
          action: interaction.action,
          feature: interaction.feature,
          value: interaction.value,
          user_type: interaction.user_type,
          session_id: this.sessionId,
        }
      })
    } catch (error) {
      logger.error('Failed to track dashboard interaction:', error)
    }
  }

  public async trackError(error: {
    error_code?: string
    error_category?: string
    surface?: string
    user_type?: string
  }): Promise<void> {
    if (!this.consentGiven) return

    try {
      await this.trackEvent({
        name: 'error_occurred',
        parameters: {
          surface: error.surface || error.error_category || 'app',
          error_code: error.error_code || 'unknown',
          user_type: error.user_type,
          session_id: this.sessionId,
        }
      })
    } catch (err) {
      logger.error('Failed to track error:', err)
    }
  }

  public async trackFeatureUsage(feature: {
    feature_name: string
    feature_category: string
    usage_count?: number
    user_type?: string
  }): Promise<void> {
    if (!this.consentGiven) return

    try {
      await this.trackEvent({
        name: 'feature_usage',
        parameters: {
          feature_name: feature.feature_name,
          feature_category: feature.feature_category,
          usage_count: feature.usage_count || 1,
          user_type: feature.user_type,
          session_id: this.sessionId,
        }
      })
    } catch (error) {
      logger.error('Failed to track feature usage:', error)
    }
  }

  public async trackConversion(conversion: {
    conversion_type: string
    conversion_value?: number
    currency?: string
    user_type?: string
  }): Promise<void> {
    if (!this.consentGiven) return

    try {
      await this.trackEvent({
        name: 'conversion',
        parameters: {
          conversion_type: conversion.conversion_type,
          conversion_value: conversion.conversion_value,
          currency: conversion.currency || 'USD',
          user_type: conversion.user_type,
          session_id: this.sessionId,
        }
      })
    } catch (error) {
      logger.error('Failed to track conversion:', error)
    }
  }

  public getSessionId(): string {
    return this.sessionId
  }

  public getSessionDuration(): number {
    return Date.now() - this.startTime
  }

  public async endSession(): Promise<void> {
    if (!this.consentGiven) return

    try {
      const sessionDuration = this.getSessionDuration()
      await this.trackUserEngagement({
        engagement_time_msec: sessionDuration,
        session_id: this.sessionId,
      })

      await this.trackEvent({
        name: 'session_end',
        parameters: {
          session_duration: sessionDuration,
          session_id: this.sessionId,
        }
      })
    } catch (error) {
      logger.error('Failed to end session:', error)
    }
  }
}

// Singleton instance
export const analyticsService = new AnalyticsService()

// Stable, module-scoped wrappers around the singleton so useAnalytics() returns
// the same function identities on every render (they close over nothing but the
// singleton). A fresh closure per render here previously fed effect deps and
// caused infinite render loops (e.g. BillingPage, CheckoutSuccessPage).
const trackEvent = (event: AnalyticsEvent) => analyticsService.trackEvent(event)
// Named trackPageViewEvent, not trackPageView: a same-named convenience
// function is exported further down this module with a different (string,
// string, string?) signature, and `const trackPageView` here would collide
// with it as a duplicate module-scope declaration.
const trackPageViewEvent = (pageView: PageViewEvent) => analyticsService.trackPageView(pageView)
const trackUserEngagement = (engagement: UserEngagementEvent) => analyticsService.trackUserEngagement(engagement)
const trackOnboardingStep = (step: OnboardingEvent) => analyticsService.trackOnboardingStep(step)
const trackDashboardInteraction = (interaction: DashboardEvent) => analyticsService.trackDashboardInteraction(interaction)
const trackError = (error: Parameters<typeof analyticsService.trackError>[0]) => analyticsService.trackError(error)
const trackFeatureUsage = (feature: Parameters<typeof analyticsService.trackFeatureUsage>[0]) => analyticsService.trackFeatureUsage(feature)
const trackConversion = (conversion: Parameters<typeof analyticsService.trackConversion>[0]) => analyticsService.trackConversion(conversion)
const setConsent = (given: boolean) => analyticsService.setConsent(given)
const hasConsent = () => analyticsService.hasConsent()

// React hook for analytics
export function useAnalytics() {
  return {
    trackEvent,
    trackPageView: trackPageViewEvent,
    trackUserEngagement,
    trackOnboardingStep,
    trackDashboardInteraction,
    trackError,
    trackFeatureUsage,
    trackConversion,
    setConsent,
    hasConsent,
    sessionId: analyticsService.getSessionId(),
    sessionDuration: analyticsService.getSessionDuration(),
  }
}

// Convenience functions for common tracking scenarios
export const trackPageView = (pageTitle: string, pagePath: string, userType?: string) => {
  analyticsService.trackPageView({
    page_title: pageTitle,
    page_location: window.location.href,
    page_path: pagePath,
    user_type: userType,
  })
}

export const trackButtonClick = (buttonName: string, location: string, userType?: string) => {
  analyticsService.trackEvent({
    name: 'button_click',
    parameters: {
      button_name: buttonName,
      location: location,
      user_type: userType,
    }
  })
}

export const trackFormSubmission = (formName: string, success: boolean, userType?: string) => {
  analyticsService.trackEvent({
    name: 'form_submission',
    parameters: {
      form_name: formName,
      success: success,
      user_type: userType,
    }
  })
}

export const trackSearch = (searchTerm: string, resultsCount: number, userType?: string) => {
  analyticsService.trackEvent({
    name: 'search',
    parameters: {
      search_term: searchTerm,
      results_count: resultsCount,
      user_type: userType,
    }
  })
}
